import React, { useCallback, useEffect } from 'react'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useNFCSState } from 'modules/nfcs/hooks/useNFCSState'
import { useNFCSModal } from 'modules/nfcs/ui/NFCSModal/useNFCSModal'

import { Text } from 'shared/ui/common/Text'
import { Modal, ModalProps } from 'modules/modal/ui/Modal'
import {
  ConnectButton,
  ConnectMetamaskButton,
  ConnectWalletConnectButton,
} from '../ConnectButton'
import { PageLoader } from 'shared/ui/layout/PageLoader'
import { useFirebase } from 'modules/analytics/firebase'
import {
  WALLET_CONNECT_EVENT,
  WALLET_MODAL_OPEN_EVENT,
} from 'modules/analytics/events'
import { useConnectedWalletName } from 'modules/wallet/hooks/useConnectedWalletName'

import { ReactComponent as BoltSVG } from 'assets/bolt.svg'

import s from './ConnectWalletModal.module.scss'

type Props = ModalProps & {}

const MODAL_WIDTH = 750

function ModalTitle({ children }: { children: React.ReactNode }) {
  return (
    <Text size={44} weight={700} isUppercased isCentered className={s.title}>
      {children}
    </Text>
  )
}

export function ConnectWalletModal(props: Props) {
  const { isWalletConnected, walletAddress } = useWeb3()
  const modalNFCS = useNFCSModal()
  const { onClose } = props
  const { nfcsStatus } = useNFCSState()
  const { trackEvent } = useFirebase()
  const connectedWalletName = useConnectedWalletName()

  useEffect(() => {
    trackEvent(WALLET_MODAL_OPEN_EVENT)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (nfcsStatus === 'generated') {
      onClose()
    }
  }, [nfcsStatus, onClose])

  const handleClickCreateNFCS = useCallback(() => {
    onClose()
    modalNFCS.open({})
  }, [modalNFCS, onClose])

  useEffect(() => {
    if (walletAddress) {
      trackEvent(WALLET_CONNECT_EVENT, { provider: connectedWalletName })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletAddress])

  if (!isWalletConnected) {
    return (
      <Modal width={MODAL_WIDTH} {...props}>
        <ModalTitle>Connect with</ModalTitle>
        <div className={s.connectors}>
          <ConnectMetamaskButton />
          <ConnectWalletConnectButton />
          {/* <ConnectLedgerButton /> */}
        </div>
      </Modal>
    )
  }

  if (nfcsStatus === 'server-error') {
    return (
      <Modal width={MODAL_WIDTH} {...props}>
        <ModalTitle>Server error</ModalTitle>
        <Text size={20} weight={400} isCentered className={s.subtitle}>
          Server error occurred while receiving the credit score
        </Text>
      </Modal>
    )
  }

  if (nfcsStatus === 'loading' || nfcsStatus === 'rate-limit') {
    return (
      <Modal width={MODAL_WIDTH} {...props}>
        <ModalTitle>Please wait</ModalTitle>
        <Text size={20} weight={400} isCentered className={s.subtitle}>
          We are checking your credit score data
        </Text>
        <PageLoader className={s.loader} />
      </Modal>
    )
  }

  return (
    <Modal width={MODAL_WIDTH} {...props}>
      <ModalTitle>Wallet Connected</ModalTitle>
      <Text
        size={16}
        weight={400}
        isCentered
        color="secondary"
        className={s.connectedText}
      >
        In order to borrow with Roci you need to{' '}
        <span className={s.highlight}>mint your NFCS token first</span>. NFCS
        is&nbsp;the&nbsp;Non-fungible Credit Score, ERC-721 token that will
        be&nbsp;minted on&nbsp;Ethereum blockchain and will generate your
        personal credit score. You&nbsp;can bundle several addresses into one
        NFCS representing your ownership of&nbsp;these addresses. By having NFCS
        in&nbsp;your wallet you can access credit at&nbsp;Roci.
      </Text>
      <div>
        <ConnectButton
          textColor="branded"
          icon={<BoltSVG />}
          onClick={handleClickCreateNFCS}
          className={s.createNFCS}
        >
          Create NFCS
        </ConnectButton>
      </div>
      <Text size={16} weight={400} isCentered className={s.connectedText}>
        This operation will&nbsp;mint NFCS token, will&nbsp;transfer it into
        your wallet and will&nbsp;trigger generation of your credit score that
        will allow you to&nbsp; access credit at&nbsp;Roci.
      </Text>
    </Modal>
  )
}
